import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';

// Template
import { LayoutDefault } from '@templates';

// UTIL
import { secondsToHHMMSS } from '@utils';

// Actions
import { actions as pageSizeActions } from '@store/ducks/pageSize';
import { actions as dashboardActions } from '@store/ducks/dashboard';
import { actions as groupActions } from '@store/ducks/group';

// Components
import {
  Title,
  Table,
  Box,
  Pagination,
  TablePagination,
  Margin,
  Button,
  FilterPageSize,
  FilterGroups,
  Search,
  NotFoundData,
  Typography,
  ShortPagination,
  DownloadButton,
} from '@components';
import useDashboardExport from '@hooks/useDashboardExport';

// Components SVG
import ArrowDown from '@icons/arrow-down.svg';

// Styles
import { StudyHoursTable, Tooltip, GoBack } from './Styled';

const StudyHours = () => {
  const router = useRouter();
  const dispatch = useDispatch();
  const [search, setSearch] = useState([]);
  const [dataTable, setDataTable] = useState([]);
  const [filterGroups, setFilterGroups] = useState([]);
  const { studyHours, loading } = useSelector(
    (state) => state.dashboard.tables
  );
  const { exportSessions } = useDashboardExport();

  const getExportParams = () => {
    const params = {};

    if (filterGroups.length) {
      params.groups = filterGroups.join(',');
    }

    if (typeof search === 'string' && search) {
      params.search = search;
    }

    return params;
  };

  const formatTimeHHMM = (number) => {
    const format = secondsToHHMMSS(number, true);
    return format.substr(0, format.length - 3);
  };

  const setModelDataTable = (arr) => {
    const dataModel = arr?.map((row, index) => ({
      raking: index + 1,
      user: row.fullname,
      time: formatTimeHHMM(parseInt(row.session)),
    }));
    return dataModel;
  };

  const getFilterGroups = (arr) => setFilterGroups(arr);

  const resetPageIndex = () => {
    const { pathname, query } = router;
    router.push({
      pathname,
      query: { ...query, page: 1 },
    });
  };

  const setSortByRanking = (arr) => {
    const toNumber = (str) => parseInt(str, 10);
    arr?.sort((a, b) => {
      if (toNumber(a.session) > toNumber(b.session)) return -1;
      if (toNumber(a.session) < toNumber(b.session)) return 1;
      return 0;
    });
    return arr;
  };

  const setSortByAlphabetical = (arr) => {
    arr?.sort((a, b) => {
      if (a.fullname.toLowerCase() < b.fullname.toLowerCase()) return -1;
      if (a.fullname.toLowerCase() > b.fullname.toLowerCase()) return 1;
      return 0;
    });
    return arr;
  };

  const handleGroupFilter = (arr) => {
    if (filterGroups.length) {
      let sortRanking = [];
      let sortAlphabetical = [];
      const filteredWithGroupsFilter = arr?.filter((obj) =>
        filterGroups.includes(obj?.group)
      );
      const sortByRanking = filteredWithGroupsFilter?.filter((obj) =>
        parseInt(obj.session)
      );
      const sortByAlphabetical = filteredWithGroupsFilter?.filter(
        (obj) => !parseInt(obj.session)
      );
      if (sortByRanking?.length) {
        sortRanking = setSortByRanking(sortByRanking);
      }
      if (sortByAlphabetical?.length) {
        sortAlphabetical = setSortByAlphabetical(sortByAlphabetical);
      }
      const sortList = [...sortRanking, ...sortAlphabetical];
      const dataModel = setModelDataTable(sortList);
      return dataModel;
    }
    return [];
  };

  const newOrderIdRanking = (filtered) => {
    return filtered.map((filter, index) => ({
      ...filter,
      raking: index + 1,
    }));
  };

  const onSearch = (value = search) => {
    setSearch(value);
    if (!filterGroups.length) return [];
    if (!value) {
      const filteredByGroup = handleGroupFilter(studyHours);
      return filteredByGroup;
    }
    if (value.length < search.length) {
      let filterData = studyHours;
      if (filterGroups.length) {
        filterData = filterData?.filter((obj) =>
          filterGroups.includes(obj?.group)
        );
      }
      const dataModel = setModelDataTable(filterData);
      const filteredOnSearch = dataModel?.filter((obj) =>
        obj?.user?.trim().toLowerCase().includes(value?.toLowerCase())
      );
      const filterById = newOrderIdRanking(filteredOnSearch);
      return filterById;
    }
    const filtered = dataTable?.filter((obj) =>
      obj?.user?.trim().toLowerCase().includes(value?.toLowerCase())
    );

    const filter = newOrderIdRanking(filtered);
    return filter;
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const filtered = onSearch(e.target.value);
    setDataTable(filtered);
  };

  const setDataByGroup = () => {
    const filteredByGroup = handleGroupFilter(studyHours);
    setDataTable(filteredByGroup);
  };

  const renderTable = (
    <Table>
      <colgroup>
        <col span="1" style={{ width: '10%' }} />
        <col span="1" style={{ width: '70%' }} />
        <col span="1" style={{ width: '20%' }} />
      </colgroup>
      <thead>
        <tr>
          <th>Ranking</th>
          <th>Usuário</th>
          <th>Horas de Estudo</th>
        </tr>
      </thead>
      <tbody>
        <TablePagination rows={dataTable} loading={loading} />
      </tbody>
    </Table>
  );

  useEffect(() => {
    dispatch(pageSizeActions.getPageSize());
    dispatch(groupActions.getAllGroupsRequest());
    dispatch(dashboardActions.getDashStudyHoursRequest());
  }, []);

  useEffect(() => {
    resetPageIndex();
  }, [search]);

  useEffect(() => {
    const filteredByGroup = handleGroupFilter(studyHours);
    if (search.length) {
      const filteredBySearch = filteredByGroup?.filter((obj) =>
        obj?.user?.toLowerCase().includes(search?.toLowerCase())
      );
      setDataTable(filteredBySearch);
    } else setDataTable(filteredByGroup);
    return resetPageIndex();
  }, [filterGroups]);

  useEffect(() => {
    if (studyHours?.length) setDataByGroup();
  }, [studyHours]);

  return (
    <LayoutDefault>
      <GoBack>
        <Button href="/home" variant="text" leftIcon={<ArrowDown />}>
          Voltar
        </Button>
      </GoBack>
      <Margin top={0.37} />
      <Title as="h1" size="3.2rem" title="Horas de estudo" />

      <Typography size="14px" color="#8C8C8C">
        Veja quantas horas de estudo foram dedicadas por cada colaborador no
        Wise Up Online, você <br />
        também pode filtrar o ranking de acordo com os grupos cadastrados.
      </Typography>

      <Margin top={1} />
      <Box>
        <Tooltip>
          <FilterGroups
            getFilterGroups={getFilterGroups}
            getType="array"
            filter="ranking"
          />
          <FilterPageSize />
          <Search
            value={search}
            onSubmit={(e) => e.preventDefault()}
            onChange={handleSearch}
            placeholder="Buscar por nome de usuário"
            withSubmit
          />
          <ShortPagination total={dataTable?.length} rightSide />
          <DownloadButton
            onDownload={() => exportSessions(getExportParams())}
            variant="text"
          />
        </Tooltip>
        <Margin />
        <StudyHoursTable
          checkPageOne={router.query.page}
          isSearch={search.length == 0 ? false : !!search}
        >
          {!dataTable?.length ? <NotFoundData /> : renderTable}
        </StudyHoursTable>
      </Box>
      <Margin top={2} />
      <Pagination total={dataTable?.length} />
    </LayoutDefault>
  );
};

export default StudyHours;
export { StudyHours };
